import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ChevronLeft, MoreHorizontal, XCircle } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Confirm } from "~/components/ui/confirm";
import { Menu } from "~/components/ui/menu";
import { Page, PageHeader, Panel, SearchInput, Segmented, Toolbar } from "~/components/ui/page";
import { ErrorState, Skeleton } from "~/components/ui/states";
import { COUNT_TONE, optionLabel } from "~/features/stock/stock";
import { api, unwrap } from "~/lib/api";
import { cn } from "~/lib/cn";
import { errorMessageKey } from "~/lib/errors";
import { useFormat, useT } from "~/lib/i18n";
import { useCan } from "~/lib/session";
import { SerialInput, useSerialTracking } from "~/features/stock/serial-input";
import { HelpTip } from "~/components/ui/help-tip";
import { helpHint } from "~/features/help/hint";

export const Route = createFileRoute("/_app/stock/counts/$id")({ component: CountPage });

type Filter = "all" | "differences" | "uncounted";

const fetchCount = (id: string) => unwrap(api.stock.counts({ id }).get());
type Count = Awaited<ReturnType<typeof fetchCount>>;

function BackLink() {
  const t = useT();
  return (
    <Link to="/stock/counts" className="mb-2 inline-flex items-center gap-1 text-sm text-fg-2 hover:text-fg">
      <ChevronLeft className="size-4" aria-hidden />
      {t("stock.tabs.counts")}
    </Link>
  );
}

function CountPage() {
  const { id } = Route.useParams();
  const query = useQuery({ queryKey: ["stock", "counts", id], queryFn: () => fetchCount(id) });
  if (query.isPending)
    return (
      <Page>
        <BackLink />
        <Skeleton className="h-8 w-72" />
        <Skeleton className="mt-6 h-96 w-full" />
      </Page>
    );
  if (query.isError)
    return (
      <Page>
        <BackLink />
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      </Page>
    );
  return <CountSheet key={query.data.updatedAt.toString()} count={query.data} />;
}

function CountSheet({ count }: { count: Count }) {
  const t = useT();
  const format = useFormat();
  const can = useCan();
  const queryClient = useQueryClient();
  const editable = count.status === "draft" && can("stock:write");
  const [draft, setDraft] = useState<Record<string, string>>(() =>
    Object.fromEntries(count.lines.map((l) => [l.variantId, l.countedQuantity === null ? "" : String(l.countedQuantity)])),
  );
  // Tracked articles are counted by their numbers: the count is how many were scanned.
  const serialTracking = useSerialTracking();
  const [serials, setSerials] = useState<Record<string, string[]>>(() =>
    Object.fromEntries(count.lines.filter((l) => l.serials).map((l) => [l.variantId, l.serials ?? []])),
  );
  const tracked = (l: Count["lines"][number]) => serialTracking && l.serialTracked;
  const [filter, setFilter] = useState<Filter>("all");
  const [q, setQ] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const counted = (variantId: string, stored: number | null) => {
    if (!editable) return stored;
    if (serialTracking && variantId in serials && count.lines.find((l) => l.variantId === variantId)?.serialTracked)
      return serials[variantId]!.length;
    const raw = draft[variantId] ?? "";
    return raw.trim() === "" || Number.isNaN(Number(raw)) ? null : Math.max(0, Math.trunc(Number(raw)));
  };
  const reference = (l: Count["lines"][number]) =>
    count.status === "validated" ? (l.expectedQuantity ?? l.bookQuantity) : l.bookQuantity;

  const needle = q.trim().toLowerCase();
  const rows = count.lines.filter((l) => {
    const c = counted(l.variantId, l.countedQuantity);
    if (filter === "uncounted" && c !== null) return false;
    if (filter === "differences" && (c === null || c === reference(l))) return false;
    return !needle || `${l.title} ${l.sku}`.toLowerCase().includes(needle);
  });

  const countedTotal = count.lines.filter((l) => counted(l.variantId, l.countedQuantity) !== null).length;
  const differences = count.lines.filter((l) => {
    const c = counted(l.variantId, l.countedQuantity);
    return c !== null && c !== reference(l);
  }).length;

  const payload = () => ({
    lines: count.lines.map((l) => ({
      variantId: l.variantId,
      countedQuantity: counted(l.variantId, l.countedQuantity),
      ...(tracked(l) ? { serials: serials[l.variantId] ?? null } : {}),
    })),
  });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["stock"] });

  const save = useMutation({
    mutationFn: () => unwrap(api.stock.counts({ id: count.id }).lines.put(payload())),
    onSuccess: () => {
      toast.success(t("stock.counts.saved"));
      void refresh();
    },
    onError: (error) => toast.error(t(errorMessageKey(error))),
  });

  const validate = useMutation({
    mutationFn: async () => {
      await unwrap(api.stock.counts({ id: count.id }).lines.put(payload()));
      return unwrap(api.stock.counts({ id: count.id }).validate.post());
    },
    onSuccess: () => {
      setConfirming(false);
      toast.success(t("stock.counts.validated"));
      void refresh();
    },
    onError: (error) => toast.error(t(errorMessageKey(error))),
  });

  const cancel = useMutation({
    mutationFn: () => unwrap(api.stock.counts({ id: count.id }).cancel.post()),
    onSuccess: () => {
      setCancelling(false);
      toast.success(t("stock.counts.cancelled"));
      void refresh();
    },
    onError: (error) => toast.error(t(errorMessageKey(error))),
  });

  return (
    <Page>
      <PageHeader
        back={<BackLink />}
        title={<span className="tabular">{count.number}</span>}
        meta={
          <span className="flex flex-wrap items-center gap-2">
            <Badge tone={COUNT_TONE[count.status]}>{t(`stock.counts.statuses.${count.status}`)}</Badge>
            <span>{format.date(count.countedAt, "long")}</span>
          </span>
        }
        actions={
          editable ? (
            <>
              <Button onClick={() => save.mutate()} loading={save.isPending}>
                {t("stock.counts.save")}
              </Button>
              <span className="inline-flex items-center gap-0.5">
                <Button variant="primary" onClick={() => setConfirming(true)} disabled={countedTotal === 0}>
                  {t("stock.counts.validate")}
                </Button>
                <HelpTip id="stock.countValidate" />
              </span>
              <Menu
                trigger={
                  <Button variant="ghost" size="icon" aria-label={t("common.actions.more")}>
                    <MoreHorizontal />
                  </Button>
                }
                actions={[
                  {
                    label: t("stock.counts.cancel"),
                    hint: helpHint(t, "stock.countCancel"),
                    icon: <XCircle />,
                    tone: "danger",
                    onSelect: () => setCancelling(true),
                  },
                ]}
              />
            </>
          ) : null
        }
      />

      <div className="mb-4">
        <div className="flex items-baseline justify-between text-sm">
          <span className="text-fg-2">{t("stock.counts.progress", { counted: countedTotal, total: count.lines.length })}</span>
          {editable ? (
            <span className="text-fg-3">
              {t("stock.counts.validateBody", { count: differences, date: format.date(count.countedAt, "long") })}
            </span>
          ) : null}
        </div>
        <progress
          value={countedTotal}
          max={Math.max(count.lines.length, 1)}
          className="mt-2 h-1.5 w-full appearance-none overflow-hidden rounded-full bg-sunken [&::-moz-progress-bar]:bg-gold [&::-webkit-progress-bar]:bg-sunken [&::-webkit-progress-value]:rounded-full [&::-webkit-progress-value]:bg-gold"
        />
      </div>

      <Panel>
        <Toolbar>
          <SearchInput value={q} onChange={setQ} placeholder={t("stock.searchPlaceholder")} />
          <Segmented<Filter>
            label={t("common.actions.filter")}
            value={filter}
            onChange={setFilter}
            options={[
              { value: "all", label: t("stock.levels.all") },
              { value: "differences", label: t("stock.counts.onlyDifferences") },
              { value: "uncounted", label: t("stock.counts.uncounted") },
            ]}
          />
        </Toolbar>
        <div className="grid grid-cols-[minmax(0,1fr)_72px_110px_72px] items-center gap-x-3 border-b border-line px-4 py-2 text-[12.5px] font-medium text-fg-3 md:px-5">
          <span>{t("stock.fields.product")}</span>
          <span className="text-right">{t("stock.counts.book")}</span>
          <span className="text-right">{t("stock.counts.counted")}</span>
          <span className="text-right">{t("stock.counts.difference")}</span>
        </div>
        <ul className="divide-y divide-line">
          {rows.map((l) => {
            const c = counted(l.variantId, l.countedQuantity);
            const ref = reference(l);
            const diff = c === null ? null : c - ref;
            return (
              <li
                key={l.variantId}
                className="grid grid-cols-[minmax(0,1fr)_72px_110px_72px] items-center gap-x-3 px-4 py-2 md:px-5"
              >
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium">{l.title}</p>
                  <p className="truncate text-xs text-fg-3">
                    <span className="tabular">{l.sku}</span>
                    {l.options.length ? ` — ${optionLabel(l.options)}` : ""}
                  </p>
                </div>
                <span className="text-right text-sm text-fg-2 tabular">{format.number(ref)}</span>
                {editable && tracked(l) ? (
                  <div className="col-span-3">
                    <SerialInput
                      value={serials[l.variantId] ?? []}
                      onChange={(next) => setSerials((s) => ({ ...s, [l.variantId]: next }))}
                      label={`${t("stock.counts.counted")} ${l.sku}`}
                    />
                  </div>
                ) : editable ? (
                  <input
                    inputMode="numeric"
                    aria-label={`${t("stock.counts.counted")} ${l.sku}`}
                    value={draft[l.variantId] ?? ""}
                    onChange={(e) => setDraft((d) => ({ ...d, [l.variantId]: e.target.value.replace(/[^\d]/g, "") }))}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        const inputs = [...document.querySelectorAll<HTMLInputElement>("input[data-count-input]")];
                        inputs[inputs.indexOf(e.currentTarget) + 1]?.focus();
                      }
                    }}
                    data-count-input
                    className="h-10 w-full rounded-control border border-line-strong bg-surface px-3 text-right text-base tabular focus:border-gold focus:ring-2 focus:ring-gold/30 focus:outline-none"
                  />
                ) : (
                  <span className="text-right text-sm tabular">{c === null ? "—" : format.number(c)}</span>
                )}
                <span
                  className={cn(
                    "text-right text-sm font-medium tabular",
                    diff === null || diff === 0 ? "text-fg-3" : diff > 0 ? "text-success" : "text-danger",
                  )}
                >
                  {diff === null ? "" : diff === 0 ? "0" : format.number(diff, { signDisplay: "always" })}
                </span>
              </li>
            );
          })}
        </ul>
      </Panel>

      <Confirm
        open={confirming}
        onOpenChange={setConfirming}
        title={t("stock.counts.validateTitle")}
        body={t("stock.counts.validateBody", { count: differences, date: format.date(count.countedAt, "long") })}
        confirmLabel={t("stock.counts.validate")}
        loading={validate.isPending}
        onConfirm={() => validate.mutate()}
      />
      <Confirm
        open={cancelling}
        onOpenChange={setCancelling}
        title={t("confirms.inventoryCancel.title")}
        body={t("confirms.inventoryCancel.body")}
        confirmLabel={t("confirms.inventoryCancel.confirm")}
        tone="danger"
        loading={cancel.isPending}
        onConfirm={() => cancel.mutate()}
      />
    </Page>
  );
}
